import React, { useRef, useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { Loader2, Lock } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { SOSSIETY_RED } from '@/styles/brand';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/pricing';

interface StripePaymentElementProps {
  clientSecret: string;
  amount: number;
  currency: string;
  instructorName: string;
  instructorId?: string;
  rollReviewId?: string;
  breakdown: {
    amount_cents: number;
    platform_fee_cents: number;
    net_to_instructor_cents: number;
    currency: string;
  };
  onSuccess: (paymentIntentId: string) => void;
  onError: (error: string) => void;
}

// ─── Card brand → gradient ─────────────────────────────────────────────────

const BRAND_GRADIENTS: Record<string, string> = {
  visa:       'linear-gradient(135deg, #1A1F71 0%, #2C5AA0 100%)',
  mastercard: 'linear-gradient(135deg, #1A1A2E 0%, #E02020 80%, #F79E1B 100%)',
  amex:       'linear-gradient(135deg, #006FCF 0%, #00A0E9 100%)',
  diners:     'linear-gradient(135deg, #0066B2 0%, #00A8E8 100%)',
  discover:   'linear-gradient(135deg, #FF6000 0%, #FFB81C 100%)',
  unknown:    `linear-gradient(135deg, ${SOSSIETY_RED} 0%, #FF6B5B 100%)`,
};

const getBrandLabel = (brand: string) => {
  const labels: Record<string, string> = {
    visa: 'VISA', mastercard: 'MASTERCARD', amex: 'AMEX',
    diners: 'DINERS', discover: 'DISCOVER',
  };
  return labels[brand] || '';
};

// ─── Stripe element options (shared style) ─────────────────────────────────

const stripeElementStyle = {
  base: {
    color: '#ffffff',
    fontSize: '15px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    '::placeholder': { color: 'rgba(255,255,255,0.35)' },
    iconColor: 'rgba(255,255,255,0.5)',
  },
  invalid: {
    color: '#ff6b6b',
    iconColor: '#ff6b6b',
  },
};

// ─── Stripe field wrapper ──────────────────────────────────────────────────

const StripeField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
      {label}
    </label>
    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 transition-colors focus-within:border-white/30">
      {children}
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────

export const StripePaymentElement: React.FC<StripePaymentElementProps> = ({
  clientSecret,
  amount,
  currency,
  instructorName,
  instructorId,
  rollReviewId,
  breakdown,
  onSuccess,
  onError,
}) => {
  const stripe   = useStripe();
  const elements = useElements();
  const { t }    = useI18n();

  const [isLoading,     setIsLoading]     = useState(false);
  const [errorMessage,  setErrorMessage]  = useState<string | null>(null);
  const [cardBrand,     setCardBrand]     = useState<string>('unknown');
  const [cardLast4,     setCardLast4]     = useState<string>('');
  const [cardholderName, setCardholderName] = useState('');
  // Prevents double-submission if the button is pressed while a confirmation is in flight
  const isConfirming = useRef(false);

  // Resolve IDs (passed as props or from URL params as fallback)
  const resolvedInstructorId = instructorId || (() => {
    try { return new URLSearchParams(window.location.search).get('instructor_id') || ''; } catch { return ''; }
  })();
  const resolvedRollReviewId = rollReviewId || (() => {
    try { return new URLSearchParams(window.location.search).get('roll_review_id') || ''; } catch { return ''; }
  })();

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCardChange = (e: any) => {
    if (e.brand) setCardBrand(e.brand === 'unknown' ? 'unknown' : e.brand);
    if (e.last4)  setCardLast4(e.last4);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (isConfirming.current) return;
    isConfirming.current = true;

    setIsLoading(true);
    setErrorMessage(null);

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setErrorMessage('Card element not found.');
      setIsLoading(false);
      return;
    }

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: cardholderName || instructorName },
        },
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        onError(error.message || 'Payment failed');
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      } else if (paymentIntent?.status === 'requires_action') {
        // 3DS — stripe handles redirect automatically
        const returnUrl = new URL(window.location.origin);
        returnUrl.pathname = `/request-roll-review/${resolvedInstructorId}`;
        returnUrl.searchParams.set('status', 'paid');
        if (resolvedRollReviewId) returnUrl.searchParams.set('roll_review_id', resolvedRollReviewId);
        window.location.href = returnUrl.toString();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unexpected error';
      setErrorMessage(msg);
      onError(msg);
    } finally {
      setIsLoading(false);
      isConfirming.current = false;
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const cardGradient = BRAND_GRADIENTS[cardBrand] ?? BRAND_GRADIENTS.unknown;
  const brandLabel   = getBrandLabel(cardBrand);
  const displayLast4 = cardLast4 ? `•••• •••• •••• ${cardLast4}` : '•••• •••• •••• ••••';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-5">

      {/* ── Card Preview ────────────────────────────────────────────── */}
      <div
        className="relative w-full rounded-2xl overflow-hidden transition-all duration-500"
        style={{ background: cardGradient, aspectRatio: '1.586 / 1' }}
      >
        {/* Glass overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />

        <div className="relative z-10 h-full flex flex-col justify-between p-6 text-white">
          {/* Top row: brand + chip */}
          <div className="flex justify-between items-start">
            <span className="text-sm font-bold tracking-widest">{brandLabel}</span>
            <div className="w-10 h-8 rounded bg-gradient-to-br from-yellow-300 to-yellow-600 opacity-80" />
          </div>

          {/* Card number */}
          <p className="text-lg font-mono tracking-[0.2em] transition-all duration-300">
            {displayLast4}
          </p>

          {/* Bottom row: name + expiry placeholder */}
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[10px] text-white/50 uppercase tracking-widest mb-0.5">Cardholder</p>
              <p className="text-sm font-semibold uppercase tracking-wide truncate max-w-[160px]">
                {cardholderName || 'Full Name'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-white/50 uppercase tracking-widest mb-0.5">Valid Thru</p>
              <p className="text-sm font-semibold">MM / YY</p>
            </div>
          </div>
        </div>

        {/* Corner glows */}
        <div className="absolute top-0 right-0 w-28 h-28 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full blur-2xl pointer-events-none" />
      </div>

      {/* ── Error ───────────────────────────────────────────────────── */}
      {errorMessage && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3">
          <p className="text-sm text-red-300">{errorMessage}</p>
        </div>
      )}

      {/* ── Form ────────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Cardholder name (plain HTML input — safe, not sensitive) */}
        <StripeField label="Name on card">
          <input
            type="text"
            value={cardholderName}
            onChange={e => setCardholderName(e.target.value)}
            placeholder="As printed on the card"
            className="w-full bg-transparent text-white text-[15px] placeholder-white/35 outline-none"
          />
        </StripeField>

        {/* Card number */}
        <StripeField label="Card number">
          <CardNumberElement
            options={{ style: stripeElementStyle, showIcon: true }}
            onChange={handleCardChange}
          />
        </StripeField>

        {/* Expiry + CVC side by side */}
        <div className="grid grid-cols-2 gap-3">
          <StripeField label="Expiry date">
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </StripeField>
          <StripeField label="CVC">
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </StripeField>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={isLoading || !stripe || !elements}
          variant="primary"
          size="lg"
          className="w-full mt-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              {t('roll_review.processing')}
            </>
          ) : (
            <>
              <Lock size={14} className="mr-2 opacity-70" />
              {t('roll_review.pay_now')} · {formatCurrency(amount, currency)}
            </>
          )}
        </Button>

        <p className="text-center text-xs text-white/30">
          Secured by Stripe. We never store card data.
        </p>
      </form>
    </div>
  );
};
